import { useQueryClient } from "@tanstack/react-query";
import { Check, Pencil, Plus, Trash2, Users, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

import { actionPlanKeys } from "../api/keys";
import {
  patchStakeholderGroup,
  useCreateStakeholderGroup,
  useDeleteStakeholderGroup,
  useStakeholderGroups,
} from "../api/hooks";
import type { StakeholderGroup } from "../api/types";
import { apiErrorDetail } from "../lib/errors";
import { Badge } from "./ui/Badge";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

/** One category: rename in place, or remove it. */
function GroupRow({ group }: { group: StakeholderGroup }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const deleteGroup = useDeleteStakeholderGroup();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(group.name);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const save = async () => {
    const next = name.trim();
    if (!next || next === group.name) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await patchStakeholderGroup(group.id, { name: next });
      await qc.invalidateQueries({ queryKey: actionPlanKeys.groups() });
      setEditing(false);
    } catch (err) {
      toast.error(apiErrorDetail(err) ?? t("actionPlans.errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <li className="flex items-center gap-2 py-1.5">
        <Input
          autoFocus
          aria-label={t("actionPlans.groups.name")}
          value={name}
          maxLength={120}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void save();
            if (e.key === "Escape") {
              setName(group.name);
              setEditing(false);
            }
          }}
          className="h-8 flex-1"
        />
        <Button size="sm" onClick={() => void save()} disabled={saving}>
          <Check aria-hidden />
          {t("actionPlans.actions.save")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setName(group.name);
            setEditing(false);
          }}
          disabled={saving}
        >
          <X aria-hidden />
        </Button>
      </li>
    );
  }

  return (
    <li className="flex items-center gap-2 py-1.5">
      <Users className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 flex-1 truncate text-sm">{group.name}</span>
      <button
        type="button"
        onClick={() => setEditing(true)}
        aria-label={t("actionPlans.groups.rename", { name: group.name })}
        title={t("actionPlans.consultations.rename")}
        className="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent [&_svg]:size-4"
      >
        <Pencil aria-hidden />
      </button>
      <button
        type="button"
        onClick={() => setConfirming(true)}
        disabled={deleteGroup.isPending}
        aria-label={t("actionPlans.groups.deleteNamed", { name: group.name })}
        title={t("actionPlans.groups.delete")}
        className="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-destructive [&_svg]:size-4"
      >
        <Trash2 aria-hidden />
      </button>
      {confirming && (
        <ConfirmDialog
          cancelLabel={t("actionPlans.actions.cancel")}
          title={t("actionPlans.groups.delete")}
          message={t("actionPlans.groups.confirmDelete", { name: group.name })}
          confirmLabel={t("actionPlans.groups.delete")}
          busy={deleteGroup.isPending}
          onConfirm={() =>
            deleteGroup.mutate(group.id, { onSettled: () => setConfirming(false) })
          }
          onClose={() => setConfirming(false)}
        />
      )}
    </li>
  );
}

/**
 * Stakeholder categories, on the step where the Django page asks for them.
 *
 * They're consulted in step 2 — that's where a category gets attached to the
 * courses of action it should weigh in on, and the consultation modal there can
 * still manage them. Defining them here first matches the legacy flow ("3.
 * Définissez les catégories de parties prenantes à consulter") and means step 2
 * opens with something to assign.
 *
 * Optional: a plan is perfectly valid with no categories at all.
 */
export function StakeholderGroupsCard() {
  const { t } = useTranslation();
  const groupsQuery = useStakeholderGroups();
  const createGroup = useCreateStakeholderGroup();
  const [name, setName] = useState("");

  const groups = groupsQuery.data ?? [];

  const add = () => {
    const next = name.trim();
    if (!next) return;
    createGroup.mutate(
      { name: next },
      {
        onSuccess: () => setName(""),
        onError: (err) =>
          toast.error(apiErrorDetail(err) ?? t("actionPlans.errors.saveFailed")),
      },
    );
  };

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-bold">{t("actionPlans.groups.heading")}</h3>
          <Badge tone="info">{t("actionPlans.groups.optional")}</Badge>
        </div>
        <p className="max-w-prose text-xs text-muted-foreground">
          {t("actionPlans.groups.stepHelp")}
        </p>

        {groups.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("actionPlans.groups.empty")}
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {groups.map((group) => (
              <GroupRow key={group.id} group={group} />
            ))}
          </ul>
        )}

        <div className="flex items-center gap-2">
          <Input
            aria-label={t("actionPlans.groups.name")}
            placeholder={t("actionPlans.groups.namePlaceholder")}
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") add();
            }}
            className="h-9 max-w-xs"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={add}
            disabled={!name.trim() || createGroup.isPending}
          >
            <Plus aria-hidden />
            {createGroup.isPending
              ? t("actionPlans.groups.adding")
              : t("actionPlans.groups.add")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
